import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { useDisclosure, useRouteMatchParam } from '@/hooks';
import React, { lazy, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import ReportDocumentHeader from '@/components/Reports/ReportPrivate/_comons/ReportDocumentHeader';
import {
  MechanicsRecordFooter,
  MechanicsRecordParts,
  MechanicsRecordTextarea,
  MechanicsRecordVehicleInfo,
} from '@/components/Reports/ReportPrivate/MechanicsRecord';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Drawer,
  Form,
  InputNumber,
  Row,
  Select,
} from 'antd';
import { useCompanies } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import moment from 'moment';
import { convertParams } from '@/utils/queryParams';
import { useCompanyReportConfig } from '@/core/domain/enterprise/Companies/CompaniesReportConfig/CompaniesReportConfig.hooks';
import { DefaultName } from '@/core/domain/reports/company/Company.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';

import './mechanicsRecord.less';
import { useLayoutEffect } from '@react-spring/shared';
import MechanicsRecordVehicleObservations from '@/components/Reports/ReportPrivate/MechanicsRecord/MechanicsRecordVehicleObservations';
import { useWorkOrderObservations } from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useCustomerVehicle } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import {
  pageSizesType,
  pageTypeSizeOptions,
  pageTypeSizes,
} from '@/types/PageSize';
import { useWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import MechanicsRecordRequestedServices from '@/components/Reports/ReportPrivate/MechanicsRecord/MechanicsRecordRequestedServices';
import MechanicsRecordPartsServices from '@/components/Reports/ReportPrivate/MechanicsRecord/MechanicsRecordPartsServices';
import MechanicsRecordObservations from '@/components/Reports/ReportPrivate/MechanicsRecord/MechanicsRecordObservations';
import MechanicsRecordTotals from '@/components/Reports/ReportPrivate/MechanicsRecord/MechanicsRecordTotals';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';

const TemplateReport = lazy(
  () => import('@/components/_common/TemplateReport/TemplateReport'),
);

export interface MechanicsRecordFormValues {
  pageTypeSize: string;
  pageSize: { x: number; y: number };
  orientation: 'landscape' | 'portrait';
  observationsRows: number;
  isObservations: boolean;
  observationServiceRows: number;
  isObservationService: boolean;
  observationLeaderRows: number;
  isObservationLeader: boolean;
  isAdditionalServicesDate: boolean;
  itemList: number;
  cardHeight: number;
  margin: number;
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  requestedServiceRows: number;
  isRequestedService: boolean;
  partRows: number;
  isParts: boolean;
  serviceRows: number;
  isServices: boolean;
  isTotals: boolean;
  isSyntheticObservations: boolean;
  items?: {
    quantity?: number;
    description?: string;
    manufacturingCode?: number;
    barcode?: number;
    companyCode?: number;
    internalCode?: number;
    sku?: number;
  }[];
}

const initialState: MechanicsRecordFormValues = {
  pageTypeSize: 'A4',
  pageSize: pageSizesType['A4'],
  orientation: 'portrait',
  itemList: 10,
  observationsRows: 6,
  isObservations: true,
  observationServiceRows: 12,
  isObservationService: true,
  observationLeaderRows: 6,
  isObservationLeader: true,
  isAdditionalServicesDate: true,
  cardHeight: 150,
  margin: 10,
  marginTop: 10,
  marginBottom: 10,
  marginLeft: 10,
  marginRight: 10,
  requestedServiceRows: 3,
  isRequestedService: true,
  partRows: 10,
  isParts: true,
  serviceRows: 10,
  isServices: true,
  isTotals: true,
  isSyntheticObservations: true,
};

const STORAGE_KEY = 'mechanicsRecordConfig';

const initialStorage = localStorage.getItem(STORAGE_KEY);

const initialFormValues = {
  ...initialState,
  ...(initialStorage ? JSON.parse(initialStorage) : {}),
};

const PrivateReportMechanicsRecord: React.FC = () => {
  const match = useRouteMatch();

  const companyIdParam = useRouteMatchParam('companyId');
  const obj = convertParams<{
    workOrderId: number;
    complementId: number;
    isSynthetic: boolean;
    print: boolean;
  }>({
    complementId: 'number',
    workOrderId: 'number',
    isSynthetic: 'boolean',
    print: 'boolean',
  });

  const [form] = Form.useForm<MechanicsRecordFormValues>();
  const formValues = Form.useWatch([], form);

  const isLandscape = formValues?.orientation === 'landscape';
  const hasPageSizeTypeCustom =
    formValues?.pageTypeSize === pageTypeSizes['Custom'];

  const [drawerConfigOpen, drawerConfigAction] = useDisclosure(false);

  const { data } = useCompanies({ rows: 50 });

  const companies = flatInfiniteData(data);

  const { data: workorderConfig, mutateAsync: workorderMutateAsync } =
    useWorkorderConfigurationCompany();

  const company = companies.find(
    (company) => company.id === Number(companyIdParam),
  );

  const { data: configCompany } = useCompanyReportConfig(companyIdParam);

  const { data: workorderData } = useWorkOrder(obj?.workOrderId);
  const { data: complementData } = useWorkOrderComplement(obj.complementId);

  const { data: vehicleData } = useCustomerVehicle(
    workorderData?.customer?.id,
    workorderData?.vehicle?.id,
  );

  const complement = workorderData?.complements.find(
    ({ id }) => id === obj?.complementId,
  );

  const { data: observations } = useWorkOrderObservations(complement?.id);

  const workOrderItemList = useWorkOrderItems(
    complement?.id,
    undefined,
    undefined,
    false,
  );

  const workOrderItemsData = workOrderItemList.data;

  document.title = `folha_do_mecânico_${moment().format('DDMMYYYY_HHmmss')}`;

  useEffect(() => {
    if (companyIdParam) workorderMutateAsync(Number(companyIdParam));
  }, [companyIdParam]);

  useEffect(() => {
    if (workOrderItemList.data) {
      form.setFieldsValue({
        items: workOrderItemsData?.data.map((item) => ({
          quantity: item.quantity,
          description: item.description,
          manufacturingCode: Number(item.manufacturingCode),
          barcode: item.barcode,
          companyCode: item.companyCode,
          internalCode: item.internalCode,
          sku: Number(item.sku),
          total: item.unitPrice * (1 - item.discount) * item.quantity,
        })),
      });
    }
  }, [workOrderItemList.dataUpdatedAt]);

  useLayoutEffect(() => {
    const css = `
      @media print {
        .print-area {
          border: none !important;
          padding: 0px !important;
          width: 100% !important;
        }


        @page {
          size: ${
            hasPageSizeTypeCustom
              ? `${formValues?.pageSize[isLandscape ? 'y' : 'x'] || 0}mm ${
                  formValues?.pageSize[isLandscape ? 'x' : 'y'] || 0
                }mm`
              : formValues?.pageTypeSize || 'auto'
          } ${!hasPageSizeTypeCustom ? formValues?.orientation : ''};

          margin-top: ${formValues?.marginTop || 0}mm;
          margin-bottom: ${formValues?.marginBottom || 0}mm;
          margin-left: ${formValues?.marginLeft || 0}mm;
          margin-right: ${formValues?.marginRight || 0}mm;
        }
    `;

    const head = document.head || document.getElementsByTagName('head')[0];
    const style = document.createElement('style');

    head.appendChild(style);

    style.setAttribute('type', 'text/css');
    style.setAttribute('id', 'mechanics-record-style-print');

    style.appendChild(document.createTextNode(css));

    return () =>
      document.getElementById('mechanics-record-style-print')?.remove();
  }, [formValues]);

  async function validateFields() {
    await form.validateFields();
  }

  const parts = Array.from({ length: formValues?.itemList || 0 }).map(
    (_, index) => index + 1,
  );

  const isMechanicSheetAutoFill = workorderConfig?.isMechanicSheetAutoFill;

  return (
    <React.Suspense fallback={<>Carregando documento...</>}>
      {match.isExact && !!company && !!configCompany && (
        <TemplateReport style={{ margin: '0px', width: '100%' }}>
          <Form
            form={form}
            initialValues={initialFormValues}
            onValuesChange={(_, values) =>
              localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
            }
          >
            <main
              className="report-mechanics-record"
              onLoad={() => {
                if (obj.print) {
                  window.print();
                }
              }}
            >
              <div className="display-none-to-print">
                <div className="report-mechanics-record-header">
                  <Row justify="space-between" gutter={[20, 20]}>
                    <Col span={24} md={6}>
                      <h2 style={{ margin: 0, padding: 0 }}>
                        Gerar Folha do Mecânico
                      </h2>
                    </Col>

                    <Col span={24} md={18}>
                      <SpaceForButton justifyContent="flex-end">
                        <Button block onClick={drawerConfigAction.open}>
                          Configurar impressão
                        </Button>

                        <Button
                          block
                          className="button-primary"
                          type="primary"
                          onClick={() => window.print()}
                        >
                          Imprimir
                        </Button>
                      </SpaceForButton>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="report-mechanics-record-content">
                <div className="print-page-info">
                  <span>
                    (
                    {hasPageSizeTypeCustom
                      ? 'Customizado'
                      : formValues?.pageTypeSize}
                    ){' '}
                    {`${
                      isLandscape
                        ? formValues?.pageSize?.y || 0
                        : formValues?.pageSize?.x || 0
                    }mmX${
                      isLandscape
                        ? formValues?.pageSize?.x || 0
                        : formValues?.pageSize?.y || 0
                    }mm`}{' '}
                    {formValues?.orientation === 'portrait'
                      ? 'Retrato'
                      : 'Paisagem'}
                  </span>
                </div>

                <div
                  className="print-area"
                  id="print-area"
                  style={{
                    paddingTop: `${formValues?.marginTop || 0}mm`,
                    paddingRight: `${formValues?.marginRight || 0}mm`,
                    paddingBottom: `${formValues?.marginBottom || 0}mm`,
                    paddingLeft: `${formValues?.marginLeft || 0}mm`,
                    width: formValues?.pageSize
                      ? `${
                          isLandscape
                            ? formValues?.pageSize?.y || 0
                            : formValues?.pageSize?.x || 0
                        }mm`
                      : undefined,
                  }}
                >
                  <ReportDocumentHeader
                    title={
                      configCompany.companyNaming === DefaultName.fantasyName
                        ? company.nickName
                        : company.name
                    }
                    company={{
                      name: 'Folha do mecânico',
                      logo: company.logo,
                    }}
                  />

                  <MechanicsRecordVehicleInfo
                    workorderData={workorderData}
                    vehicleData={vehicleData}
                    complement={complementData}
                  />

                  {!obj.isSynthetic && (
                    <>
                      {formValues.isObservations && (
                        <MechanicsRecordVehicleObservations
                          observationsRows={formValues.observationsRows}
                          observations={observations}
                        />
                      )}

                      {formValues.isObservationService && (
                        <MechanicsRecordTextarea
                          title="Serviços complementares"
                          rows={formValues.observationServiceRows || 12}
                        />
                      )}

                      {formValues.isObservationLeader && (
                        <MechanicsRecordTextarea
                          title="Observações do Lider"
                          rows={formValues.observationLeaderRows || 6}
                        />
                      )}

                      {isMechanicSheetAutoFill &&
                        workOrderItemsData?.data.map((item, index) => (
                          <>
                            <div key={index} className="part">
                              <div>
                                <strong>{index + 1}</strong>
                              </div>
                              <MechanicsRecordParts
                                hasItem={!!item}
                                index={index}
                              />
                            </div>
                          </>
                        ))}

                      {isMechanicSheetAutoFill && (
                        <Divider children="Itens em branco" />
                      )}

                      {parts.map((_, index) => (
                        <div key={index} className="part">
                          <div>
                            <strong>
                              {(isMechanicSheetAutoFill
                                ? workOrderItemsData?.data.length || 0
                                : 0) +
                                index +
                                1}
                            </strong>
                          </div>
                          <MechanicsRecordParts
                            index={
                              (workOrderItemsData?.data.length || 0) + index
                            }
                          />
                        </div>
                      ))}

                      <MechanicsRecordFooter
                        isAdditionalServicesDate={
                          formValues.isAdditionalServicesDate
                        }
                      />
                    </>
                  )}

                  {obj.isSynthetic && (
                    <>
                      {formValues.isRequestedService && (
                        <MechanicsRecordRequestedServices
                          rows={formValues.requestedServiceRows}
                        />
                      )}

                      {formValues.isRequestedService && (
                        <MechanicsRecordPartsServices
                          label="Peças"
                          rows={formValues.partRows}
                          item={workOrderItemsData?.data.filter(
                            (item) => item.type === WorkOrderItemType.Part,
                          )}
                          isMechanicSheetAutoFill={isMechanicSheetAutoFill}
                        />
                      )}

                      {formValues.isRequestedService && (
                        <MechanicsRecordPartsServices
                          label="Serviços"
                          rows={formValues.serviceRows}
                          item={workOrderItemsData?.data.filter(
                            (item) => item.type === WorkOrderItemType.Service,
                          )}
                          isMechanicSheetAutoFill={
                            workorderConfig?.isMechanicSheetAutoFill
                          }
                        />
                      )}

                      {formValues.isTotals && <MechanicsRecordTotals />}

                      {formValues.isSyntheticObservations && (
                        <MechanicsRecordObservations />
                      )}
                    </>
                  )}
                </div>
              </div>
            </main>

            <Drawer
              title="Configuração de impressão"
              placement="right"
              open={drawerConfigOpen}
              onClose={drawerConfigAction.close}
              destroyOnClose={false}
              width={600}
            >
              <>
                <div className="m-b-20">
                  <strong>Layout da página</strong>
                </div>

                <Row gutter={[16, 16]}>
                  <Col span={24} md={12}>
                    <FloatingFormItem
                      label="Tipo de impressão"
                      name="pageTypeSize"
                      getValueFromEvent={(value) => {
                        if (value !== pageTypeSizes['Custom']) {
                          form.setFieldsValue({
                            pageSize: pageSizesType[value],
                          });
                        }
                        validateFields();
                        return value;
                      }}
                    >
                      <Select
                        options={pageTypeSizeOptions}
                        style={{ width: '100%' }}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12}>
                    <FloatingFormItem label="Orientação" name="orientation">
                      <Select
                        options={[
                          { value: 'landscape', label: 'Paisagem' },
                          { value: 'portrait', label: 'Retrato' },
                        ]}
                        style={{ width: '100%' }}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12}>
                    <FloatingFormItem
                      label="Largura da página (mm)"
                      name={['pageSize', 'x']}
                      rules={[
                        {
                          type: 'number',
                          required: formValues?.pageTypeSize === 'Custom',
                          message: '',
                        },
                      ]}
                    >
                      <InputDecimal
                        style={{ width: '100%' }}
                        min={0}
                        disabled={!hasPageSizeTypeCustom}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12}>
                    <FloatingFormItem
                      label="Altura da página (mm)"
                      name={['pageSize', 'y']}
                      rules={[
                        {
                          type: 'number',
                          required: formValues?.pageTypeSize === 'Custom',
                          message: '',
                        },
                      ]}
                    >
                      <InputDecimal
                        style={{ width: '100%' }}
                        min={0}
                        disabled={!hasPageSizeTypeCustom}
                      />
                    </FloatingFormItem>
                  </Col>
                </Row>

                <Divider />

                {!obj.isSynthetic && (
                  <>
                    <div className="m-b-20">
                      <strong>Observações / Câmbio / Blindado</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de Observações"
                          name="observationsRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={2}
                            max={20}
                            disabled={!formValues.isObservations}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '6'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isObservations"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Divider />

                    <div className="m-b-20">
                      <strong>Serviços complementares</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de Serviços Complementares"
                          name="observationServiceRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            max={50}
                            disabled={!formValues.isObservationService}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '12'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isObservationService"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Divider />

                    <div className="m-b-20">
                      <strong>Observações do Lider</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de Observações do Líder"
                          name="observationLeaderRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            disabled={!formValues.isObservationLeader}
                            max={50}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '6'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isObservationLeader"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Divider />

                    <div className="m-b-20">
                      <strong>Itens em branco</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <FloatingFormItem
                          label="Lista de Itens em branco"
                          name="itemList"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={0}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '10'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24}>
                        <FloatingFormItem
                          name="isAdditionalServicesDate"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>
                            Exibir Serviços Adicionais / Detail S.C.S / Quimicos
                            / Datas
                          </Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>
                  </>
                )}

                {obj.isSynthetic && (
                  <>
                    <div className="m-b-20">
                      <strong>Serviços solicitados</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de serviços solicitados"
                          name="requestedServiceRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            max={20}
                            disabled={!formValues.isRequestedService}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '3'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isRequestedService"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Divider />

                    <div className="m-b-20">
                      <strong>Peças em branco</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de peças em branco"
                          name="partRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            max={20}
                            disabled={!formValues.isParts}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '10'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isParts"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Divider />

                    <div className="m-b-20">
                      <strong>Serviços em branco</strong>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={24} md={12}>
                        <FloatingFormItem
                          label="Linhas de serviços em branco"
                          name="serviceRows"
                        >
                          <InputNumber<number>
                            style={{ width: '100%' }}
                            min={1}
                            max={20}
                            disabled={!formValues.isServices}
                            formatter={(value) =>
                              value ? Math.floor(value).toString() : '10'
                            }
                            parser={(value) => Number(value)}
                          />
                        </FloatingFormItem>
                      </Col>

                      <Col span={24} md={12}>
                        <FloatingFormItem
                          name="isServices"
                          valuePropName="checked"
                          style={{ marginTop: 12 }}
                        >
                          <Checkbox>Exibir</Checkbox>
                        </FloatingFormItem>
                      </Col>
                    </Row>

                    <Col span={24}>
                      <FloatingFormItem
                        name="isTotals"
                        valuePropName="checked"
                        style={{ marginTop: 12 }}
                      >
                        <Checkbox>Exibir totais</Checkbox>
                      </FloatingFormItem>
                    </Col>

                    <Col span={24}>
                      <FloatingFormItem
                        name="isSyntheticObservations"
                        valuePropName="checked"
                        style={{ marginTop: 12 }}
                      >
                        <Checkbox>Exibir observações</Checkbox>
                      </FloatingFormItem>
                    </Col>
                  </>
                )}

                <Divider />

                <div className="m-b-20">
                  <strong>Margens da folha</strong>
                </div>

                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <FloatingFormItem
                      label="Todo os lados (mm)"
                      name="allMarginSides"
                      getValueFromEvent={(value) => {
                        form.setFieldsValue({
                          marginBottom: value,
                          marginLeft: value,
                          marginRight: value,
                          marginTop: value,
                        });
                        return value;
                      }}
                    >
                      <InputDecimal
                        style={{ width: '100%' }}
                        min={0}
                        max={50}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24}>
                    <Row justify="center">
                      <Col span={12}>
                        <FloatingFormItem
                          label="Superiro (mm)"
                          name="marginTop"
                        >
                          <InputDecimal
                            style={{ width: '100%' }}
                            min={0}
                            max={50}
                          />
                        </FloatingFormItem>
                      </Col>
                    </Row>
                  </Col>

                  <Col span={24} md={12}>
                    <FloatingFormItem label="Esquerda (mm)" name="marginLeft">
                      <InputDecimal
                        style={{ width: '100%' }}
                        min={0}
                        max={50}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={12}>
                    <FloatingFormItem label="Direita (mm)" name="marginRight">
                      <InputDecimal
                        style={{ width: '100%' }}
                        min={0}
                        max={50}
                      />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24}>
                    <Row justify="center">
                      <Col span={12}>
                        <FloatingFormItem
                          label="Inferiro (mm)"
                          name="marginBottom"
                        >
                          <InputDecimal
                            style={{ width: '100%' }}
                            min={0}
                            max={50}
                          />
                        </FloatingFormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </>

              <Divider />

              <div className="report-product-tags-drawer-info m-b-20">
                <strong>
                  <small>Legendas</small>
                </strong>

                <span className="-red">Área de impressão</span>

                <span className="-gray">Quebra de página</span>
              </div>

              <Divider />

              <Button
                onClick={() => {
                  localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify(initialState),
                  );

                  form.setFieldsValue(initialState);
                }}
                type="primary"
                block
              >
                Resetar Configurações
              </Button>
            </Drawer>

            <Form.Item hidden name="pageTypeSize" />
            <Form.Item hidden name="pageSize" />
            <Form.Item hidden name="itemList" />
            <Form.Item hidden name="observationsRows" />
            <Form.Item hidden name="isObservations" />
            <Form.Item hidden name="observationServiceRows" />
            <Form.Item hidden name="isObservationService" />
            <Form.Item hidden name="observationLeaderRows" />
            <Form.Item hidden name="isObservationLeader" />
            <Form.Item hidden name="isAdditionalServicesDate" />
            <Form.Item hidden name="orientation" />
            <Form.Item hidden name="cardHeight" />
            <Form.Item hidden name="margin" />
            <Form.Item hidden name="marginTop" />
            <Form.Item hidden name="marginBottom" />
            <Form.Item hidden name="marginLeft" />
            <Form.Item hidden name="marginRight" />
            <Form.Item hidden name="requestedServiceRows" />
            <Form.Item hidden name="isRequestedService" />
            <Form.Item hidden name="partRows" />
            <Form.Item hidden name="isParts" />
            <Form.Item hidden name="serviceRows" />
            <Form.Item hidden name="isServices" />
            <Form.Item hidden name="isTotals" />
            <Form.Item hidden name="isSyntheticObservations" />
            <Form.Item hidden name="quantity" />
            <Form.Item hidden name="description" />
            <Form.Item hidden name="manufacturingCode" />
            <Form.Item hidden name="barcode" />
            <Form.Item hidden name="companyCode" />
            <Form.Item hidden name="internalCode" />
            <Form.Item hidden name="sku" />
          </Form>
        </TemplateReport>
      )}
    </React.Suspense>
  );
};

export default PrivateReportMechanicsRecord;
